/**
 * React Query Client Configuration
 *
 * Centralized configuration for TanStack React Query.
 * Used for server state management with automatic caching,
 * refetching, and request deduplication.
 *
 * Includes localStorage persistence for instant navigation
 * and Firestore subscription integration for real-time updates.
 */

import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { createFirestoreSerializer } from './firestoreSerializer';
import { safeLocalStorage } from '@/services/utils/safeStorage';

// ============================================
// QUERY KEY FACTORY
// ============================================

/**
 * Query key factory for type-safe and consistent query keys.
 * Organized by feature/section for easy cache invalidation.
 */
export const queryKeys = {
  // Admin users queries
  admin: {
    all: ['admin'] as const,
    users: {
      all: ['admin', 'users'] as const,
      list: (filters: Record<string, unknown>) => ['admin', 'users', 'list', filters] as const,
      detail: (userId: string) => ['admin', 'users', 'detail', userId] as const,
      stats: (userId: string) => ['admin', 'users', 'stats', userId] as const,
    },
    inviteCodes: {
      all: ['admin', 'inviteCodes'] as const,
      list: () => ['admin', 'inviteCodes', 'list'] as const,
    },
  },

  // Study section queries
  study: {
    all: ['study'] as const,
    checkIn: (userId: string) => ['study', 'checkIn', userId] as const,
    portion: (parshaTitle: string) => ['study', 'portion', parshaTitle] as const,
    files: (fileIds: string[]) => ['study', 'files', fileIds.sort().join(',')] as const,
  },

  // Chat section queries
  chat: {
    all: ['chat'] as const,
    community: () => ['chat', 'community'] as const,
    broadcasts: () => ['chat', 'broadcasts'] as const,
    leaders: () => ['chat', 'leaders'] as const,
  },

  // Direct messages queries
  dm: {
    all: ['dm'] as const,
    conversations: (userId: string) => ['dm', 'conversations', userId] as const,
    messages: (conversationId: string) => ['dm', 'messages', conversationId] as const,
  },

  // Library section queries
  library: {
    all: ['library'] as const,
    files: () => ['library', 'files'] as const,
    tags: () => ['library', 'tags'] as const,
    file: (fileId: string) => ['library', 'file', fileId] as const,
  },

  // Prayers section queries
  prayers: {
    all: ['prayers'] as const,
    byType: (type: string) => ['prayers', type] as const,
  },

  // User profile queries
  profile: {
    all: ['profile'] as const,
    stats: (userId: string) => ['profile', 'stats', userId] as const,
  },
} as const;

// ============================================
// PERSISTENCE CONFIGURATION
// ============================================

// Cache version - increment to bust cache on breaking changes
const CACHE_BUSTER = 'v2';

// Max age for persisted cache (24 hours)
const MAX_AGE = 1000 * 60 * 60 * 24;

// Create custom serializer for Firestore Timestamps
const firestoreSerializer = createFirestoreSerializer();

/**
 * LocalStorage persister for React Query cache.
 * Uses safeLocalStorage for Safari Private Mode compatibility.
 * Handles Firestore Timestamp serialization automatically.
 */
export const queryPersister = createSyncStoragePersister({
  // Use safeLocalStorage which gracefully falls back to in-memory storage
  // when localStorage is unavailable (Safari Private Mode, quota exceeded)
  storage: safeLocalStorage,
  key: `spirit-of-shabbat-query-cache-${CACHE_BUSTER}`,
  serialize: firestoreSerializer.serialize,
  deserialize: (data: string) => firestoreSerializer.deserialize(data) as PersistedClient,
  // Throttle writes to localStorage (1 second)
  throttleTime: 1000,
});

// ============================================
// QUERY CLIENT CONFIGURATION
// ============================================

/**
 * Create the React Query client with optimized defaults.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 30 seconds
        staleTime: 30 * 1000,
        // Keep unused data in cache for 10 minutes (increased for better navigation)
        gcTime: 10 * 60 * 1000,
        // Don't refetch on window focus (we use Firestore subscriptions for real-time)
        refetchOnWindowFocus: false,
        // Retry failed requests up to 2 times
        retry: 2,
        // Exponential backoff for retries
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      mutations: {
        // Retry mutations once on failure
        retry: 1,
      },
    },
  });
}

/**
 * Singleton query client instance.
 * This is created once and reused across the app.
 */
export const queryClient = createQueryClient();

/**
 * Persistence options for PersistQueryClientProvider
 */
export const persistOptions = {
  persister: queryPersister,
  maxAge: MAX_AGE,
  // Don't persist error states
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { state: { status: string } }) => {
      return query.state.status === 'success';
    },
  },
};

/**
 * Clear all cached data (query cache + image cache).
 * Call this on logout to prevent data leaks between users.
 */
export async function clearAllCaches(): Promise<void> {
  // Clear React Query cache
  queryClient.clear();

  // Clear localStorage persisted cache
  if (typeof window !== 'undefined') {
    const { clearImageCache } = await import('./imageCache');
    clearImageCache();

    // Remove the persisted query cache from localStorage
    // Uses safeLocalStorage for Safari Private Mode compatibility
    safeLocalStorage.removeItem(`spirit-of-shabbat-query-cache-${CACHE_BUSTER}`);
  }
}

export default queryClient;
